import React from "react"

import EventCard from "@/components/common/EventCard"

// Define a base event type that contains the minimum properties needed
interface BaseEventType {
  id: string
  date: Date
  title: string
  status: "Open" | "Closed" | "Deleted"
  location: string
  timeSlot?: string
  startTime?: string
  endTime?: string
  capacity: number
  registered: number
  recurrence?: string
}

// The card's own details are optional: callers that return something else fall
// back to EventCard's defaults.
interface EventCardDetails {
  title?: React.ReactNode
  badge?: React.ReactNode
  attendeeIcon?: React.ReactNode
  attendeeInfo?: React.ReactNode
  actionLabel?: string
  bookingDisabled?: boolean
}

const GridEventDisplay = <TEvent extends BaseEventType>({
  events,
  onOpenEvent,
  renderCardContent,
  emptyMessage,
}: {
  events: TEvent[]
  onOpenEvent: (event: TEvent) => void
  renderCardContent: (event: TEvent) => EventCardDetails | React.ReactNode
  emptyMessage: string
}) => {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {events.length > 0 ? (
        events.map((event) => {
          const details = renderCardContent(event)
          const { badge, attendeeIcon, attendeeInfo, actionLabel, bookingDisabled } =
            React.isValidElement(details) ? ({} as EventCardDetails) : (details as EventCardDetails)

          return (
            <div key={event.id} onClick={() => onOpenEvent(event)} className="cursor-pointer">
              <EventCard
                date={event.date}
                location={event.location}
                timeInfo={event.timeSlot || `${event.startTime} - ${event.endTime}`}
                capacity={event.capacity}
                registered={event.registered}
                actionLabel={actionLabel}
                bookingDisabled={bookingDisabled}
                title={event.title}
                status={event.status}
                attendeeIcon={attendeeIcon}
                attendeeInfo={attendeeInfo}
                badge={badge}
              />
            </div>
          )
        })
      ) : (
        <div className="col-span-full text-center py-8">
          <p className="text-palette-jungleGreen500">{emptyMessage}</p>
        </div>
      )}
    </div>
  )
}

export default GridEventDisplay
